import { createStore } from 'vuex'
import axios from 'axios'
import { convertPrice, formatPrice, getRate, getSymbol } from '@/utils/currency'
import { createPersistPlugin } from './plugins/persist'

const API_BASE = process.env.VUE_APP_API_URL || '/api'

/**
 * 清理 localStorage 中所有以 `${prefix}_` 开头的 key。
 * 用法：clearAllUserKeys('footprints') 会删 footprints_1 / footprints_user_5 / footprints_temp 等
 */
function clearAllUserKeys(prefix) {
  try {
    const keys = Object.keys(localStorage)
    const toRemove = keys.filter(k => k === prefix || k.startsWith(prefix + '_') || k.startsWith(prefix + ':'))
    toRemove.forEach(k => localStorage.removeItem(k))
    if (toRemove.length) {
      console.log(`[Store] Cleared ${toRemove.length} localStorage keys with prefix "${prefix}"`)
    }
  } catch (e) {
    console.warn('[Store] clearAllUserKeys failed:', e)
  }
}

// 定义需要持久化的状态路径（不包含 token，token 在 mutation 中单独处理）
const PERSIST_PATHS = ['user', 'cart', 'favorites', 'locale', 'currency']

// 从 localStorage 恢复 token（页面刷新后）
function getInitialToken() {
  const token = localStorage.getItem('token')
  console.log('[Store Init] localStorage token raw value:', token)
  // 清理 token：去除首尾空白和引号
  const cleanToken = token ? token.trim().replace(/^"|"$/g, '') : ''
  // 验证 token 是否有效（非空、非 null 字符串、非 undefined 字符串）
  if (cleanToken && cleanToken !== 'null' && cleanToken !== 'undefined' && cleanToken.length > 0) {
    console.log('[Store Init] Restoring token, length:', cleanToken.length)
    // 设置 axios 默认请求头
    axios.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`
    return cleanToken
  }
  console.log('[Store Init] No valid token found')
  return null
}

export default createStore({
  state: {
    user: null,
    token: getInitialToken(),
    cart: [],
    favorites: [],
    locale: 'en',
    currency: 'USD',
    categories: [],
    banners: [],
    selectedCoupon: null
  },

  getters: {
    isLoggedIn: state => !!state.token,
    cartCount: state => state.cart.reduce((sum, item) => sum + item.quantity, 0),
    cartTotalUSD: state => {
      return state.cart.reduce((sum, item) => {
        const price = item.selectedPrice || item.price
        return sum + price * item.quantity
      }, 0)
    },
    cartTotal: (state, getters) => {
      return convertPrice(getters.cartTotalUSD, state.currency)
    },
    favoriteCount: state => state.favorites.length,
    currentCurrency: state => state.currency,
    currencyRate: state => getRate(state.currency),
    currencySymbol: state => getSymbol(state.currency)
  },

  mutations: {
    SET_USER(state, user) {
      // 清除旧的头像缓存，确保切换用户时不会显示上一个用户的头像
      if (user && user.avatar) {
        localStorage.setItem('app_user_avatar', user.avatar)
      } else if (user && !user.avatar) {
        localStorage.removeItem('app_user_avatar')
      }
      state.user = user
    },
    SET_TOKEN(state, token) {
      state.token = token
      // 同时保存到 localStorage，确保刷新页面后登录状态不丢失
      if (token && token !== 'null' && token !== 'undefined') {
        localStorage.setItem('token', token)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      } else {
        localStorage.removeItem('token')
        delete axios.defaults.headers.common['Authorization']
      }
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = null
      localStorage.removeItem('token')
      localStorage.removeItem('app_token')
      // 登出时也清掉用户地址（防止下一个登入的人看到上一位的地址）
      localStorage.removeItem('user_addresses')
      // 清掉所有账号的足迹 localStorage 分片
      clearAllUserKeys('footprints')
      // 清空购物车、收藏、优惠卷（防止下一位登入用户看到上一个人的数据）
      state.cart = []
      state.favorites = []
      state.coupons = []
      state.userCoupons = []
      state.orders = []
      state.unreadChat = 0
      localStorage.removeItem('cart')
      localStorage.removeItem('favorites')
      localStorage.removeItem('user_coupons')
      localStorage.removeItem('orders')
      delete axios.defaults.headers.common['Authorization']
    },
    SET_CART(state, cart) {
      state.cart = cart
    },
    ADD_TO_CART(state, item) {
      const existing = state.cart.find(i => i.id === item.id && i.sku === item.sku)
      if (existing) {
        existing.quantity += item.quantity
      } else {
        state.cart.push({ ...item, selected: true })
      }
    },
    REMOVE_FROM_CART(state, { id, sku }) {
      state.cart = state.cart.filter(i => !(i.id === id && i.sku === sku))
    },
    UPDATE_CART_ITEM(state, { id, sku, quantity }) {
      const item = state.cart.find(i => i.id === id && i.sku === sku)
      if (item) item.quantity = quantity
    },
    SET_FAVORITES(state, favorites) {
      state.favorites = favorites
    },
    TOGGLE_FAVORITE(state, product) {
      const idx = state.favorites.findIndex(f => f.id === product.id)
      if (idx > -1) {
        state.favorites.splice(idx, 1)
      } else {
        state.favorites.push(product)
      }
    },
    SET_LOCALE(state, locale) {
      state.locale = locale
    },
    SET_CURRENCY(state, currency) {
      state.currency = currency
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_BANNERS(state, banners) {
      state.banners = banners
    },
    SET_SELECTED_COUPON(state, coupon) {
      state.selectedCoupon = coupon
    },
    CLEAR_SELECTED_COUPON(state) {
      state.selectedCoupon = null
    }
  },

  actions: {
    async login({ commit }, credentials) {
      try {
        // globalshop API: POST /api/auth/login -> {success, data:{token, user}}
        const { data } = await axios.post(`${API_BASE}/auth/login`, credentials)
        if (data.success && data.data && data.data.token) {
          commit('SET_TOKEN', data.data.token)
          commit('SET_USER', data.data.user)
          // 登录成功后清掉 guest 期间留在 localStorage 的地址，避免新账号继承上次的"垃圾地址"
          localStorage.removeItem('user_addresses')
          // 足迹：迁移/清理
          migrateFootprintsOnLogin(data.data.user)
          // 登录成功后把用户当前使用的语言同步到后端（让客服侧能识别用户所在国家）
          try {
            const curLocale = localStorage.getItem('locale') || 'en'
            await axios.put(`${API_BASE}/users/me/locale`, { preferredLocale: curLocale })
          } catch (e) { /* 静默失败 */ }
          return data
        } else {
          const err = new Error(data.error || data.message || '登录失败')
          err.response = { data }
          throw err
        }
      } catch (err) {
        if (err.response && err.response.data) {
          const e = new Error(err.response.data.error || err.response.data.message || '登录失败')
          e.response = err.response
          throw e
        }
        throw err
      }
    },
    async register({ commit }, userData) {
      // globalshop API: POST /api/auth/register -> {success, data:{token, user}}
      const { data } = await axios.post(`${API_BASE}/auth/register`, userData)
      if (data.success && data.data && data.data.token) {
        commit('SET_TOKEN', data.data.token)
        commit('SET_USER', data.data.user)
      } else {
        // 兜底：尝试从顶层 token/user 取（兼容未来接口）
        if (data.token) commit('SET_TOKEN', data.token)
        if (data.user) commit('SET_USER', data.user)
      }
      // 注册成功后清掉 guest 期间留在 localStorage 的地址
      localStorage.removeItem('user_addresses')
      // 足迹：迁移/清理
      const u = data.data?.user || data.user
      if (u) migrateFootprintsOnLogin(u)
      return data
    },
    async fetchCurrentUser({ commit, state }) {
      // 只要有 token 就从服务器拉取最新用户信息（包含积分、余额、头像等）
      // 这样可以避免使用持久化插件恢复的过期数据
      // globalshop API: GET /api/auth/me -> {success, data: user}
      if (!state.token) return null
      try {
        const { data } = await axios.get(`${API_BASE}/auth/me`)
        if (data && data.success && data.data) {
          commit('SET_USER', data.data)
          return data.data
        }
      } catch (err) {
        console.warn('[Store] fetchCurrentUser failed:', err.message)
      }
      return state.user
    },
    logout({ commit }) {
      commit('CLEAR_AUTH')
      commit('SET_CART', [])
      commit('SET_FAVORITES', [])
    },
    async fetchCategories({ commit }) {
      const { data } = await axios.get(`${API_BASE}/categories`)
      commit('SET_CATEGORIES', data)
      return data
    },
    async fetchBanners({ commit }) {
      const { data } = await axios.get(`${API_BASE}/banners`)
      commit('SET_BANNERS', data)
      return data
    }
  },

  plugins: [
    createPersistPlugin({ paths: PERSIST_PATHS })
  ]
})

/**
 * 登录/注册成功后，处理 localStorage 里历史足迹的迁移与清理
 * 场景：之前以 guest 身份浏览过很多商品，那些记录都存在 footprints_guest。
 *       现在登入到 A 账号：A 应当继承这些浏览历史；
 *       但 guest 之后又被 B 注册登录时，B 不应继承 A 的历史。
 * 解决方案：
 *  - 若目标账号专属 key 已存在：不动（保留原本属于该账号的足迹）
 *  - 若目标账号专属 key 不存在：把 footprints_guest 的内容"移交"过去，并把 guest key 清空
 *  - 旧的共享 key `footprints`（老数据）也清理掉
 */
function migrateFootprintsOnLogin(user) {
  try {
    if (!user || !user.id) return
    const userKey = getUserKeyByUser('footprints', user)
    const existing = localStorage.getItem(userKey)
    if (existing) return // 该账号已有自己的足迹，不动
    const guestRaw = localStorage.getItem('footprints_guest')
    if (guestRaw) {
      localStorage.setItem(userKey, guestRaw)
      localStorage.removeItem('footprints_guest')
    }
    // 老版本（共享 key 'footprints'）直接清掉，避免脏数据残留
    localStorage.removeItem('footprints')
  } catch (err) {
    // ignore
  }
}
